import React from 'react';

const STYLES = `
  @keyframes omnidrive-reveal {
    0%, 10%  { opacity: 0; transform: scale(0.75); }
    20%, 80% { opacity: 1; transform: scale(1);    }
    90%, 100%{ opacity: 0; transform: scale(0.75); }
  }

  @keyframes omnidrive-bolt {
    0%, 100% { opacity: 1;   filter: drop-shadow(0 0 4px #facc15) brightness(1);   }
    50%      { opacity: 0.8; filter: drop-shadow(0 0 10px #facc15) brightness(1.4); }
  }

  @keyframes omnidrive-spin {
    from { transform: rotate(0deg);   }
    to   { transform: rotate(360deg); }
  }

  /* Each letter pops in sequentially, sized relative to the SVG */
  .od-char {
    opacity: 0;
    animation: omnidrive-reveal 2.4s ease-in-out infinite;
    font-family: 'Space Grotesk', sans-serif;
    font-weight: 900;
    fill: #eab308;
    font-size: 13px;
  }

  .od-bolt {
    animation: omnidrive-bolt 1.5s ease-in-out infinite;
  }

  .od-frame {
    animation: omnidrive-spin 12s linear infinite;
    transform-origin: 60px 60px;
  }
`;

/*
  Letter positions calculated on a circle:
  centre (60,60), radius 50, starting at -90° (12 o'clock), 9 letters × 40° steps.
  x = 60 + 50·cos(θ),  y = 60 + 50·sin(θ) + 5 (SVG text baseline offset)
*/
const LETTERS = [
  { char: 'O', x: 60,   y: 15,   delay: '0.0s' },
  { char: 'M', x: 92,   y: 27,   delay: '0.2s' },
  { char: 'N', x: 109,  y: 56,   delay: '0.4s' },
  { char: 'i', x: 103,  y: 90,   delay: '0.6s' },
  { char: 'D', x: 77,   y: 111,  delay: '0.8s' },
  { char: 'R', x: 43,   y: 111,  delay: '1.0s' },
  { char: 'I', x: 17,   y: 90,   delay: '1.2s' },
  { char: 'V', x: 11,   y: 56,   delay: '1.4s' },
  { char: 'E', x: 28,   y: 27,   delay: '1.6s' },
];

function OmniDriveIcon({ size = 140 }) {
  return (
    <svg viewBox="0 0 120 120" width={size} height={size}>
      {/* Rotating dual-plug frame – dark so it's visible on any background */}
      <g className="od-frame">
        <path d="M95 35 A 45 45 0 0 1 60 105" fill="none" stroke="#1e293b" strokeWidth="3" strokeLinecap="round" />
        <path d="M25 85 A 45 45 0 0 1 60 15"  fill="none" stroke="#1e293b" strokeWidth="3" strokeLinecap="round" />
        {/* Plug tips */}
        <rect x="90" y="28" width="8" height="5" fill="#1e293b" rx="1" transform="rotate(-35 95 31)" />
        <rect x="22" y="86" width="8" height="5" fill="#1e293b" rx="1" transform="rotate(145 25 89)" />
      </g>

      {/* Central lightning bolt */}
      <path
        d="M65 40 L48 60 L58 60 L55 80 L72 55 L62 55 Z"
        fill="#facc15"
        className="od-bolt"
      />

      {/* Letters revealed one-by-one, mathematically placed on the circle */}
      {LETTERS.map(({ char, x, y, delay }) => (
        <text
          key={char + delay}
          x={x}
          y={y}
          textAnchor="middle"
          className="od-char"
          style={{ animationDelay: delay }}
        >
          {char}
        </text>
      ))}
    </svg>
  );
}

export default function LoadingSpinner({ message = 'Loading...', fullScreen = false }) {
  const spinner = (
    <div className="flex flex-col items-center justify-center gap-3">
      <style>{STYLES}</style>
      <OmniDriveIcon size={fullScreen ? 160 : 120} />
      {message && (
        <p
          className="text-xs font-semibold uppercase text-gray-400"
          style={{ fontFamily: 'monospace', letterSpacing: '0.35em' }}
        >
          {message}
        </p>
      )}
    </div>
  );

  if (fullScreen) {
    return (
      <div className="fixed inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center z-50">
        {spinner}
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center py-16">
      {spinner}
    </div>
  );
}
